import React from "react";
import { FiChevronDown, FiGrid, FiList, FiSearch } from "react-icons/fi";
import { LuArrowUpDown } from "react-icons/lu";

import { FILE_SORT_KEYS } from "../utilities/sortFiles";
import type { FileSortKey } from "../utilities/sortFiles";
import { FILE_VIEW_MODES } from "../utilities/fileViewModes";
import type { FileViewMode } from "../utilities/fileViewModes";
import DropdownMenu from "./DropdownMenu";
import styles from "../FileExplorer.module.scss";

interface FileListToolbarProps {
  searchText: string;
  onSearchTextChange: (searchText: string) => void;
  sortKey: FileSortKey;
  onSortKeyChange: (sortKey: FileSortKey) => void;
  viewMode: FileViewMode;
  onViewModeChange: (viewMode: FileViewMode) => void;
}

const SORT_LABELS: Record<FileSortKey, string> = {
  date: "Date",
  name: "Name",
  extension: "Extension",
};

const VIEW_MODE_LABELS: Record<FileViewMode, string> = {
  list: "List",
  gallery: "Gallery",
};

const VIEW_MODE_ICONS: Record<FileViewMode, React.ReactNode> = {
  list: <FiList aria-hidden="true" />,
  gallery: <FiGrid aria-hidden="true" />,
};

interface ChoiceMenuProps<Choice extends string> {
  /** Names the menu, and starts the button's label. */
  label: string;
  icon: React.ReactNode;
  choices: readonly Choice[];
  choiceLabels: Record<Choice, string>;
  currentChoice: Choice;
  onChoose: (choice: Choice) => void;
}

/** A toolbar button that opens a menu of choices, with the current one checked. */
function ChoiceMenu<Choice extends string>({
  label,
  icon,
  choices,
  choiceLabels,
  currentChoice,
  onChoose,
}: ChoiceMenuProps<Choice>) {
  return (
    <DropdownMenu
      buttonLabel={`${label}: ${choiceLabels[currentChoice]}`}
      buttonClassName={styles.toolbarMenuButton}
      buttonContent={
        <>
          {icon}
          <FiChevronDown
            className={styles.toolbarMenuChevron}
            aria-hidden="true"
          />
        </>
      }
      menuLabel={label}
      items={choices.map((choice) => ({
        label: choiceLabels[choice],
        onSelect: () => onChoose(choice),
        isCurrentChoice: choice === currentChoice,
      }))}
    />
  );
}

/**
 * The line above the file explorer's files: a search field, then the Sort and
 * View buttons. `FileList` holds their state.
 */
export default function FileListToolbar({
  searchText,
  onSearchTextChange,
  sortKey,
  onSortKeyChange,
  viewMode,
  onViewModeChange,
}: FileListToolbarProps) {
  return (
    <div className={styles.toolbar}>
      <div className={styles.searchField}>
        <FiSearch aria-hidden="true" />
        <input
          type="search"
          className={styles.searchInput}
          value={searchText}
          onChange={(event) => onSearchTextChange(event.target.value)}
          placeholder="Search files"
          aria-label="Search files"
        />
      </div>
      <ChoiceMenu
        label="Sort by"
        icon={<LuArrowUpDown aria-hidden="true" />}
        choices={FILE_SORT_KEYS}
        choiceLabels={SORT_LABELS}
        currentChoice={sortKey}
        onChoose={onSortKeyChange}
      />
      <ChoiceMenu
        label="View"
        icon={VIEW_MODE_ICONS[viewMode]}
        choices={FILE_VIEW_MODES}
        choiceLabels={VIEW_MODE_LABELS}
        currentChoice={viewMode}
        onChoose={onViewModeChange}
      />
    </div>
  );
}
